import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import {
  ArrowLeftIcon, CheckCircleIcon, ClockIcon, DocumentArrowUpIcon,
  ArrowTopRightOnSquareIcon, ExclamationCircleIcon, BanknotesIcon,
  DocumentTextIcon, UsersIcon,
} from '@heroicons/react/24/outline';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { DATE_FLOOR, completionMax, expiryMax } from '../../lib/dateBounds';

// Steps that get an inline fillable form in the portal instead of the
// generic "upload your document" UI. Matched by template title because
// we don't have a more durable identifier on onboarding_templates yet.
const INLINE_FORM_STEPS = {
  'Direct Deposit Form':     { route: '/my/direct-deposit', icon: BanknotesIcon, cta: 'Fill out Direct Deposit form' },
  'W-4':                     { route: '/my/w4',             icon: DocumentTextIcon, cta: 'Fill out W-4 form' },
  'Emergency Contact Form':  { route: '/my/emergency-contact', icon: UsersIcon, cta: 'Fill out Emergency Contact form' },
};

// Candidate-facing onboarding checklist. Groups by phase, shows deadline
// countdowns, lets the candidate upload docs and enter completion +
// expiration dates per step. Admin-uploader steps are read-only here.

const STATUS_META = {
  pending:   { label: 'To do',    cls: 'bg-gray-100 text-gray-600' },
  completed: { label: 'Submitted', cls: 'bg-blue-100 text-blue-700' },
  verified:  { label: 'Verified',  cls: 'bg-green-100 text-green-700' },
};

function deadlineDate(step, user) {
  if (!step.deadline_days || !step.deadline_anchor) return null;
  const base = step.deadline_anchor === 'first_training_day'
    ? user.first_training_day
    : user.start_date;
  if (!base) return null;
  const d = new Date(base);
  d.setDate(d.getDate() + step.deadline_days);
  return d;
}

function deadlineLabel(step, user) {
  const d = deadlineDate(step, user);
  if (!d) return null;
  const now = new Date();
  const days = Math.ceil((d - now) / (24 * 60 * 60 * 1000));
  if (days < 0) return { text: `Past due by ${-days}d`, urgency: 'red' };
  if (days <= 3) return { text: `Due in ${days}d`, urgency: 'amber' };
  return { text: `Due ${d.toLocaleDateString()}`, urgency: 'gray' };
}

export default function MyOnboardingPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openStepId, setOpenStepId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/my/onboarding');
      setData(data);
    } catch {
      toast.error('Could not load your checklist.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="text-gray-400">Loading…</div></div>;
  if (!data) return null;

  const phase1 = data.steps.filter(s => s.phase === 'phase_1');
  const phase2 = data.steps.filter(s => s.phase === 'phase_2');

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <Toaster position="top-right" />
      <div className="max-w-3xl mx-auto">
        <Link to="/my" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeftIcon className="w-4 h-4" /> Back
        </Link>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">Your checklist</h1>
        <p className="text-gray-500 text-sm mb-6">
          Steps marked "admin" are handled by Bright Beginnings. The rest are yours — upload docs and enter completion dates as you finish each one.
        </p>

        <PhaseSection
          title="Phase 1 — Pre-start Clearance"
          steps={phase1}
          user={data.user}
          authUserId={user?.id}
          openStepId={openStepId}
          setOpenStepId={setOpenStepId}
          onUpdated={load}
        />

        <div className="mt-8">
          <PhaseSection
            title="Phase 2 — Onboarding"
            subtitle="Items you can also start now if you already have your certificates."
            steps={phase2}
            user={data.user}
            authUserId={user?.id}
            openStepId={openStepId}
            setOpenStepId={setOpenStepId}
            onUpdated={load}
          />
        </div>

        {/* What-happens-next footer — gives the candidate a clear
            mental model of where they are after they save a step.
            Without this the page just "ends" and they wonder if
            they missed something. */}
        <NextStepsFooter steps={data.steps} />
      </div>
    </div>
  );
}

// Tail of the page — tells the candidate what happens after they
// finish. Three states:
//   - All verified → "You're cleared, see you on [start_date]."
//   - All submitted (waiting on admin) → "Admin is reviewing."
//   - Mixed → "Keep going, here's how it works."
function NextStepsFooter({ steps }) {
  const total = steps.length;
  const verified = steps.filter(s => s.status === 'verified').length;
  const submitted = steps.filter(s => s.status === 'completed').length;
  const allDone = total > 0 && verified === total;
  const adminReviewing = total > 0 && (verified + submitted) === total && !allDone;

  if (allDone) {
    return (
      <div className="mt-8 rounded-2xl border border-green-200 bg-green-50/60 p-5">
        <h3 className="font-semibold text-green-900 mb-1">You're cleared. ✓</h3>
        <p className="text-sm text-green-800">
          Everything's verified. Watch your email — your director will send
          first-day details (when to arrive, where to park, what to bring).
        </p>
        <p className="text-xs text-green-700 mt-2">
          Stuck? Call the daycare at <a href="tel:5748551604" className="underline font-semibold">(574) 855-1604</a>{' '}
          or message Sandra or Lavonn on GroupMe.
        </p>
      </div>
    );
  }
  if (adminReviewing) {
    return (
      <div className="mt-8 rounded-2xl border border-blue-200 bg-blue-50/60 p-5">
        <h3 className="font-semibold text-blue-900 mb-1">Submitted — admin is reviewing.</h3>
        <p className="text-sm text-blue-800">
          You've turned in everything. Sandra or Lavonn (BBC admins) will
          check each item. If anything needs a fix you'll get an email
          asking for a redo. Otherwise we'll text you when your file is
          fully cleared.
        </p>
        <p className="text-xs text-blue-700 mt-2">
          Questions? Call the daycare at <a href="tel:5748551604" className="underline font-semibold">(574) 855-1604</a>{' '}
          or message Sandra or Lavonn on GroupMe.
        </p>
      </div>
    );
  }
  return (
    <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-5">
      <h3 className="font-semibold text-gray-900 mb-1">Keep going.</h3>
      <p className="text-sm text-gray-600">
        Knock out one item at a time — order doesn't matter. When you submit
        an item, admin reviews it and either approves it or sends it back
        with a note. You'll get an email either way.
      </p>
      <p className="text-xs text-gray-500 mt-2">
        Stuck? Call the daycare at <a href="tel:5748551604" className="underline font-semibold">(574) 855-1604</a>{' '}
        or message Sandra or Lavonn on GroupMe.
      </p>
    </div>
  );
}

function PhaseSection({ title, subtitle, steps, user, authUserId, openStepId, setOpenStepId, onUpdated }) {
  const done = steps.filter(s => s.status === 'verified').length;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider">{title}</h2>
        <span className="text-xs text-gray-500">{done} / {steps.length} verified</span>
      </div>
      {subtitle && <p className="text-xs text-gray-500 mb-3">{subtitle}</p>}
      <div className="space-y-2">
        {steps.map(s => (
          <StepRow
            key={s.progress_id}
            step={s}
            user={user}
            authUserId={authUserId}
            isOpen={openStepId === s.progress_id}
            onToggle={() => setOpenStepId(openStepId === s.progress_id ? null : s.progress_id)}
            onUpdated={onUpdated}
          />
        ))}
      </div>
    </div>
  );
}

function StepRow({ step, user, authUserId, isOpen, onToggle, onUpdated }) {
  const adminOwned = step.uploader === 'admin';
  const verified = step.status === 'verified';
  const meta = STATUS_META[step.status] || STATUS_META.pending;
  const dl = deadlineLabel(step, user);

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left p-4 flex items-start gap-3 hover:bg-gray-50"
      >
        <div className="flex-shrink-0 mt-0.5">
          {verified ? (
            <CheckCircleIcon className="w-6 h-6 text-green-500" />
          ) : (
            <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-medium text-gray-900">{step.step_name}</p>
              <p className="text-sm text-gray-600 mt-0.5">{step.description}</p>
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
              {adminOwned && <span className="text-xs text-gray-500">Admin handles</span>}
              {dl && (
                <span className={`text-xs flex items-center gap-1 ${
                  dl.urgency === 'red' ? 'text-red-600' :
                  dl.urgency === 'amber' ? 'text-amber-600' : 'text-gray-500'
                }`}>
                  <ClockIcon className="w-3 h-3" /> {dl.text}
                </span>
              )}
            </div>
          </div>
        </div>
      </button>
      {isOpen && (
        <div className="border-t border-gray-100 p-4 bg-gray-50">
          {adminOwned ? (
            <AdminOwnedDetail step={step} />
          ) : (
            <StepEditor step={step} authUserId={authUserId} onUpdated={onUpdated} />
          )}
        </div>
      )}
    </div>
  );
}

function AdminOwnedDetail({ step }) {
  if (step.status === 'verified') {
    return (
      <p className="text-sm text-green-700">
        Verified on {step.verified_at ? new Date(step.verified_at).toLocaleDateString() : '—'}.
      </p>
    );
  }
  return (
    <p className="text-sm text-gray-600">
      Your admin will handle this and update its status here. You don't need to upload anything yourself.
    </p>
  );
}

function InlineFormCTA({ step, inline }) {
  const Icon = inline.icon;
  const done = step.status === 'completed' || step.status === 'verified';
  if (done) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-green-700">
          <CheckCircleIcon className="w-5 h-5" />
          <p className="text-sm font-medium">
            Submitted {step.completed_at ? `on ${new Date(step.completed_at).toLocaleDateString()}` : ''}.
          </p>
        </div>
        {step.document_url && (
          <a href={step.document_url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm text-orange-600 hover:underline">
            <ArrowTopRightOnSquareIcon className="w-4 h-4" /> View submitted PDF
          </a>
        )}
        <Link to={inline.route} className="block text-xs text-gray-500 hover:text-gray-700 underline">
          Re-open the form (read-only if already submitted)
        </Link>
      </div>
    );
  }
  return (
    <Link to={inline.route}
      className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-2.5 rounded-xl">
      <Icon className="w-4 h-4" /> {inline.cta}
    </Link>
  );
}

// Provenance values we are willing to pre-fill from. Anything else gets shown
// beside an empty field instead of inside it. Mirrors migration 071.
const TRUSTED_PROVENANCE = new Set(['document', 'computed_from_standard']);

const PROVENANCE_BLURB = {
  transcribed_unverified: 'from the paper staff log \u2014 unverified, nobody has checked it against a document',
  derived_from_template:  'worked out from how long this training normally lasts \u2014 not from your certificate',
  none:                   'we do not have a source for this date',
};

// Shows a stored date the portal does not trust, BESIDE the field rather than
// inside it. Ruled 2026-09-11: a blank field must never mean both "this does
// not expire" and "nobody looked", and a date nobody has checked must never
// arrive pre-confirmed.
function StoredDateHint({ value, provenance }) {
  if (!value) return null;
  return (
    <p className="text-xs text-amber-700 mt-1">
      We have <strong>{value}</strong> on file &mdash;{' '}
      {PROVENANCE_BLURB[provenance] || PROVENANCE_BLURB.none}.
      {' '}Please check it against your document and type what the document says.
    </p>
  );
}

function StepEditor({ step, authUserId, onUpdated }) {
  // Some steps have a custom inline form (Direct Deposit, eventually
  // W-4) — route the candidate there instead of showing the generic
  // upload UI.
  const inline = INLINE_FORM_STEPS[step.step_name];
  if (inline) {
    return <InlineFormCTA step={step} inline={inline} />;
  }

  // STEP 0.75 (build order, 2026-09-11). NO PRE-FILLED DATES FROM UNTRUSTED
  // PROVENANCE.
  //
  // Both of these used to be seeded from the stored value — which, for most
  // rows, is a migration-026 transcription of the paper BBFC log that nobody
  // has ever checked against a document. Ruled by Ramal Taylor 2026-09-11:
  //
  //   "A pre-filled date gets confirmed by reflex, and a paper-log guess ends
  //    up wearing a director's name."
  //
  // The stored value is not hidden — it is shown BESIDE the empty field,
  // labelled with where it came from (see StoredDateHint below). The staff
  // member reads their document and types what it says. If the two agree,
  // nothing is lost; if they disagree, we find out.
  //
  // A date already traced to a document IS pre-filled, because re-typing a
  // checked date is friction with no benefit.
  const storedCompletion = step.external_completion_date?.slice(0, 10) || '';
  const storedExpiration = step.expiration_date?.slice(0, 10) || '';
  const dateIsTraced = TRUSTED_PROVENANCE.has(step.expiry_provenance || 'none');
  const [completionDate, setCompletionDate] = useState(dateIsTraced ? storedCompletion : '');
  const [expirationDate, setExpirationDate] = useState(dateIsTraced ? storedExpiration : '');
  // NEVER SEEDED FROM `step.notes`. Ruled 2026-09-12.
  //
  // This textarea used to open pre-filled with the row's internal note — the
  // engineering record: provenance, rulings, recovery reasoning, other people's
  // names, dead row ids. A staff member could read all of it on their own page
  // and save something else over it. The server no longer sends the field at
  // all; this starts empty so the box is what it claims to be, a place for the
  // person to say something about what they are uploading.
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  // STEP 3 (build order, 2026-09-11) — THE RENEWAL LOCK IS GONE.
  //
  // A `locked` flag disabled the file input, the date fields and the Save
  // button on any verified step. It existed because an upload used to DESTROY
  // the card the requirement was standing on, in the same write that demoted
  // it. With the child table (migration 073) an upload onto a verified
  // requirement is filed as a sibling and the requirement does not move — so
  // there is nothing left to gate, and someone who renews early can upload the
  // day they get the card.
  //
  // What replaces the lock is a SENTENCE, not a disabled control: the person is
  // told their current document keeps counting until a director checks the new
  // one. Do not reintroduce a lock or an early-renewal window here.
  const isVerified = step.status === 'verified';

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      if (file) fd.append('document', file);
      // The unified file endpoint addresses items by template_id so it
      // works even before a progress row exists. Maps completion_date /
      // expiration_date the same way as before.
      fd.append('template_id', String(step.template_id));
      if (completionDate) fd.append('completion_date', completionDate);
      if (expirationDate) fd.append('expiration_date', expirationDate);
      if (notes)          fd.append('notes', notes);
      // Self-uploads stay 'completed' (not auto-verified) so admin
      // still reviews before the step counts.
      fd.append('auto_verify', 'false');

      // Phase 1.5 consolidation: every upload routes through
      // /api/file/:userId/upload, which writes file_audit_log.
      // Replaces the legacy /api/my/onboarding/:progressId path.
      if (!authUserId) throw new Error('Auth missing — please reload.');
      const { data } = await api.post(`/file/${authUserId}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // A replacement filed against a verified requirement did NOT change the
      // requirement, and the person has to be told that in so many words —
      // otherwise "Saved" on a page that still reads Verified looks like the
      // upload was ignored.
      toast.success(
        data?.pending_replacement
          ? 'Uploaded. Your current document still counts until a director checks this one.'
          : 'Saved'
      );
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      {step.external_url && (
        <a href={step.external_url} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm text-orange-600 hover:underline">
          Open the link <ArrowTopRightOnSquareIcon className="w-4 h-4" />
        </a>
      )}

      {isVerified && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2">
          <p className="text-xs text-green-900">
            <strong>This is verified and counts.</strong> If you have renewed it, you can
            upload the new document now — you do not have to wait. Your current one keeps
            counting until a director checks the new one.
          </p>
        </div>
      )}

      {step.requires_upload && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            <DocumentArrowUpIcon className="w-4 h-4 inline -mt-0.5" />
            {isVerified ? ' Upload a replacement' : ' Upload document'}
          </label>
          {step.document_url && (
            <p className="text-xs text-gray-500 mb-1">
              Already uploaded: <a href={step.document_url} target="_blank" rel="noreferrer" className="text-orange-600 hover:underline">view</a> (you can replace it).
            </p>
          )}
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full text-sm text-gray-600"
          />
        </div>
      )}

      {step.requires_completion_date && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Completion date</label>
          <input
            type="date" min={DATE_FLOOR} max={completionMax()}
            value={completionDate}
            onChange={(e) => setCompletionDate(e.target.value)}
            className="bg-white border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-orange-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            The date printed on your certificate. Leave blank if it does not say.
          </p>
          {!dateIsTraced && (
            <StoredDateHint value={storedCompletion} provenance={step.expiry_provenance} />
          )}
        </div>
      )}

      {step.requires_expiration_date && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Expiration date</label>
          <input
            type="date" min={DATE_FLOOR} max={expiryMax()}
            value={expirationDate}
            onChange={(e) => setExpirationDate(e.target.value)}
            className="bg-white border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-orange-500"
          />
          {!dateIsTraced && (
            <StoredDateHint value={storedExpiration} provenance={step.expiry_provenance} />
          )}
          {!expirationDate && step.validity_months && (
            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <ExclamationCircleIcon className="w-3.5 h-3.5" />
              Leave blank to auto-set ({step.validity_months} months from completion).
            </p>
          )}
        </div>
      )}

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Notes (optional)</label>
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full bg-white border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-orange-500"
        />
      </div>

      <button type="submit" disabled={saving}
        className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/40 text-white text-sm font-medium px-4 py-2 rounded-lg">
        {saving ? 'Saving…' : isVerified ? 'Upload replacement' : 'Save step'}
      </button>
    </form>
  );
}
